import { useState, useEffect } from 'react'
import { Modal, Button } from 'Common/Supplyvue-ui'
import { Box, CircularProgress, useTheme } from '@mui/material'
import _ from 'lodash'

import { Loader } from 'Common/Loader/Loader'
import { Page } from 'Common/Page/Page'
import { Accordions } from './Accordions/Accordions.view'
import {
  SourceSiteDetails,
  WorkcentreModalProps,
} from './WorkcentreModal.types'
import { SourceSiteSelectionControl } from './SourceSiteSelectionControl/SourceSiteSelectionControl.view'
import { sourceSitesCodeSort, sourceSitesLabelSort } from 'Common/utils/utils'
import {
  isAnyWorkCentresSelected,
  isAnySourceSitesSelected,
} from './WorkcentreModal.utils'

export const WorkcentreModal = ({
  isModalOpen,
  handleModalClose,
  sourceSiteData,
  handleOkbuttonClick,
  readOnly,
  isUpdating,
  templateSourceSiteDetails = [],
}: WorkcentreModalProps) => {
  const theme = useTheme()
  const [sourceSiteDetails, setSourceSiteDetails] = useState(sourceSiteData)

  useEffect(() => {
    sourceSiteData.sort(sourceSitesLabelSort)
    const sorted = sourceSiteData.map((sourceSite: SourceSiteDetails) => {
      const siteWorkcentres =
        sourceSite.siteWorkcentres.toSorted(sourceSitesCodeSort)
      return {
        ...sourceSite,
        siteWorkcentres,
      }
    })
    setSourceSiteDetails(sorted)
  }, [sourceSiteData])

  useEffect(() => {
    if (!templateSourceSiteDetails.length) return
    const sites = templateSourceSiteDetails.map(
      (sourceSite) => sourceSite.siteCode
    )

    const siteWorkcentres = templateSourceSiteDetails.map((sourceSite) => {
      return sourceSite?.siteWorkcentres?.map((workcentre) => workcentre.code)
    })
    const sitesSet = new Set(sites)
    const siteWorkcentresSet = new Set(siteWorkcentres.flat())

    const updatedData = sourceSiteDetails.map((sourcesite) => {
      const isSelected = sitesSet.has(sourcesite.siteCode)
      const workcentres = sourcesite.siteWorkcentres.map((workcentre) => {
        return {
          ...workcentre,
          isSelected: siteWorkcentresSet.has(workcentre.code),
        }
      })
      return {
        ...sourcesite,
        isSelected,
        siteWorkcentres: workcentres.toSorted(sourceSitesCodeSort),
      }
    })

    updatedData.sort(sourceSitesLabelSort)
    setSourceSiteDetails(updatedData)
  }, [templateSourceSiteDetails])

  const handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    sourceSiteName?: string,
    workcentreName?: string
  ) => {
    const updatedData = sourceSiteDetails.map((sourcesite) => {
      if (sourceSiteName === sourcesite.siteLabel || !sourceSiteName) {
        const workcentres = sourcesite.siteWorkcentres.map((workcentre) => {
          if (
            (workcentreName && workcentre.code === workcentreName) ||
            !workcentreName ||
            !sourceSiteName
          ) {
            return {
              ...workcentre,
              isSelected:
                workcentre.productCount > 0 ? event.target.checked : false,
            }
          } else return workcentre
        })
        return {
          ...sourcesite,
          isSelected: isAnyWorkCentresSelected(workcentres),
          siteWorkcentres: workcentres,
        }
      } else return sourcesite
    })
    setSourceSiteDetails(updatedData)
  }

  const Body = (
    <Box
      sx={{
        minWidth: '700px',
        height: readOnly ? 'calc(50vh + 110px)' : '50vh',
      }}
    >
      {!readOnly && (
        <SourceSiteSelectionControl
          sourceSiteDetails={sourceSiteDetails}
          handleCheckboxChange={handleCheckboxChange}
        />
      )}
      <Box
        sx={{
          overflowY: 'scroll',
          height: readOnly ? '100%' : 'calc(100% - 60px)',
        }}
      >
        <Accordions
          readOnly={readOnly}
          sourceSiteDetails={sourceSiteDetails}
          handleCheckboxChange={handleCheckboxChange}
        />
      </Box>
    </Box>
  )

  const isSourceSiteDetailsEdited = () => {
    return !_.isEqual(sourceSiteDetails, sourceSiteData)
  }

  const Loading = (
    <Box
      sx={{
        minWidth: '700px',
        height: readOnly ? 'calc(50vh + 110px)' : '50vh',
      }}
    >
      <Page center>
        <Loader />
      </Page>
    </Box>
  )

  const Footer = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'flex-end',
        position: 'sticky',
      }}
    >
      <Button
        onClick={handleModalClose}
        sx={{
          marginLeft: '20px',
          minWidth: '105px',
        }}
        variant="outlined"
        label="Cancel"
        disabled={isUpdating}
      >
        Cancel
      </Button>
      <Button
        disabled={
          !isAnySourceSitesSelected(sourceSiteDetails) ||
          isUpdating ||
          !isSourceSiteDetailsEdited()
        }
        variant="contained"
        onClick={() => handleOkbuttonClick(sourceSiteDetails)}
        sx={{
          marginLeft: '20px',
          minWidth: '105px',
        }}
        label="Ok"
      >
        {isUpdating && (
          <CircularProgress
            color="inherit"
            size={16}
            sx={{
              marginRight: '8px',
            }}
          />
        )}
        Ok
      </Button>
    </Box>
  )

  return (
    <Modal
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return
        }
        // Handle other close events
        handleModalClose()
      }}
      aria-labelledby={readOnly ? 'View work centres' : 'Select work centres'}
      open={isModalOpen}
      handleClose={handleModalClose}
      header={readOnly ? 'View work centres' : 'Select work centres'}
      body={sourceSiteData.length > 0 ? Body : Loading}
      footer={!readOnly && Footer}
      sx={{
        background: theme.palette.grey[100],
      }}
      isHeaderCloseButtonDisabled={isUpdating}
    />
  )
}
